import React, { useState, useEffect } from 'react';
import { paymentAPI, GST_CHARGE, NET_HANDELING } from '../api/paymentApi';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const PaymentDialog = ({ isOpen, onClose, courseInfo, onPaymentSuccess }) => {
  const { userInfo } = useAuth();
  const [enterCoupon, setEnterCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponResponse, setCouponResponse] = useState(null);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when dialog closes
      setEnterCoupon(false);
      setCouponCode('');
      setCouponResponse(null);
      setDiscountedPrice(0);
      setDiscount(0);
    }
  }, [isOpen]);

  if (!isOpen || !courseInfo) return null;

  const coursePrice = courseInfo.price - (courseInfo.price * (GST_CHARGE + NET_HANDELING)) / 100;
  const totalPrice = discountedPrice || courseInfo.price;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    try {
      setLoading(true);
      const response = await paymentAPI.getCoupons(courseInfo.id, couponCode);
      
      if (response.error === false && response.data) {
        setCouponResponse(response);
        setDiscountedPrice(response.payableAmount);
        setDiscount(response.data.off);
        toast.success('Coupon applied successfully!');
      } else {
        setCouponResponse(response);
        toast.error(response.message || 'Invalid coupon code');
      }
    } catch (error) {
      console.error('Coupon error:', error);
      toast.error('Failed to apply coupon');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!userInfo?.user_detail) {
      toast.error('Please login to continue');
      return;
    }

    const purchaseVal = {
      name: userInfo.user_detail.username,
      email: userInfo.user_detail.email,
      mobile: userInfo.user_detail.phone,
      uid: userInfo.user_detail.id,
    };

    console.log('Initiating payment for course:', {
      courseId: courseInfo.id,
      courseName: courseInfo.sub_name,
      price: discountedPrice || courseInfo.price,
      userId: userInfo.user_detail.id
    });

    try {
      setLoading(true);
      
      await paymentAPI.initializeRazorpay(
        courseInfo,
        purchaseVal,
        discountedPrice,
        discount,
        couponCode,
        (result, paymentId) => {
          console.log('Payment Success:', { paymentId, result });
          toast.success(`Payment Successful! Transaction ID: ${paymentId}`);
          onPaymentSuccess && onPaymentSuccess(result);
          onClose();
        },
        (error) => {
          console.error('Payment failed:', error);
          toast.error(error || 'Payment failed');
        }
      );
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
            <h3 className="text-2xl font-bold text-center">Payment Details</h3>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Course Price */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Course Price:</span>
              <span className="text-gray-900 font-bold">₹ {coursePrice.toFixed(2)}</span>
            </div>

            {/* Net Handling Cost */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Net Handling Cost:</span>
              <span className="text-gray-900 font-bold">{NET_HANDELING}%</span>
            </div>

            {/* GST */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">G.S.T:</span>
              <span className="text-gray-900 font-bold">{GST_CHARGE}%</span>
            </div>

            <div className="border-t border-gray-200 my-4"></div>

            {/* Total Pay */}
            <div className="flex justify-between items-center">
              <span className="text-lg text-gray-700 font-bold">Total Pay:</span>
              <span className="text-2xl text-green-600 font-bold">₹ {totalPrice}</span>
            </div>

            {/* Discount Display */}
            {couponResponse && couponResponse.data && (
              <div className="flex justify-between items-center bg-green-50 p-3 rounded-lg">
                <span className="text-green-700 font-medium">Discount:</span>
                <span className="text-green-700 font-bold">{discount}% OFF</span>
              </div>
            )}

            {/* Coupon Section Toggle */}
            <div className="text-center my-4">
              <button
                onClick={() => setEnterCoupon(!enterCoupon)}
                className="text-red-600 hover:text-red-700 font-bold text-sm transition"
              >
                {enterCoupon ? 'Hide Coupon' : 'Apply Coupon?'}
              </button>
            </div>

            {/* Coupon Input */}
            {enterCoupon && (
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  disabled={discountedPrice > 0}
                />

                {/* Coupon Error Message */}
                {couponResponse && couponResponse.error && (
                  <p className="text-red-600 font-bold text-sm">
                    {couponResponse.message}
                  </p>
                )}

                {/* Apply/Applied Button */}
                {discountedPrice > 0 ? (
                  <button
                    className="w-full bg-red-500 text-white font-bold py-2 px-4 rounded-lg cursor-not-allowed"
                    disabled
                  >
                    Coupon Applied ✓
                  </button>
                ) : (
                  <button
                    onClick={handleApplyCoupon}
                    disabled={loading || !couponCode.trim()}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Applying...' : 'Apply Coupon'}
                  </button>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handlePurchase}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Purchase'}
              </button>
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

            {/* Payment Info */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Secure payment powered by Razorpay
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentDialog;

